/**
 * Brand Setup Password Component
 * Allows brand owners to set their initial password after receiving an invite
 */

import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import '../assets/css/brand-portal.css';

// Use environment variable or default based on environment
const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (process.env.NODE_ENV === 'production') {
    // Use the Heroku backend URL in production
    return 'https://yemberzal-app-28481d1d0b39.herokuapp.com';
  }
  return 'http://localhost:8000';
};

const API_URL = getApiUrl();

function BrandSetupPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [brandInfo, setBrandInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Verify invite token on mount
  useEffect(() => {
    const verifyInvite = async () => {
      try {
        const response = await fetch(`${API_URL}/api/brand/verify-invite/${token}/`);
        const data = await response.json();

        if (response.ok && data.valid) {
          setBrandInfo(data);
        } else {
          setError(data.error || 'Invalid invite link');
        }
      } catch (err) {
        setError('Failed to verify invite. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    verifyInvite();
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setValidationErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const errors = {};

    if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/brand/setup-password/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          invite_token: token,
          password: formData.password,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/brand-portal/login');
        }, 3000);
      } else {
        setError(data.error || 'Failed to set password');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="brand-auth-page">
        <div className="brand-auth-container">
          <div className="brand-auth-card" style={{ textAlign: 'center' }}>
            <div className="brand-spinner" style={{ width: 40, height: 40, margin: '0 auto 16px' }} />
            <p style={{ color: '#6b6560' }}>Verifying your invite...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !brandInfo) {
    return (
      <div className="brand-auth-page">
        <div className="brand-auth-container">
          <div className="brand-auth-card">
            <div className="brand-auth-header">
              <Link to="/">
                <img
                  src={require('../assets/images/Y.png')}
                  alt="Yemberzal"
                  className="brand-auth-logo"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = '/default-image.avif';
                  }}
                />
              </Link>
              <h1 className="brand-auth-title">Invalid Invite</h1>
            </div>
            
            <div className="brand-alert brand-alert-error">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {error}
            </div>

            <Link to="/brand-portal/login" className="brand-btn brand-btn-primary" style={{ textDecoration: 'none', marginTop: 16 }}>
              Go to Login
            </Link>

            <div className="brand-auth-footer">
              <Link to="/contact" style={{ color: 'inherit' }}>
                Need help? Contact Support
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="brand-auth-page">
        <div className="brand-auth-container">
          <div className="brand-auth-card">
            <div className="brand-auth-header">
              <div style={{ 
                width: 64, 
                height: 64, 
                background: 'rgba(34, 197, 94, 0.1)', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                margin: '0 auto 24px'
              }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              </div>
              <h1 className="brand-auth-title">Password Set!</h1>
              <p className="brand-auth-subtitle">
                Your account is ready. Redirecting you to login...
              </p>
            </div>

            <Link to="/brand-portal/login" className="brand-btn brand-btn-primary" style={{ textDecoration: 'none' }}>
              Continue to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="brand-auth-page">
      <div className="brand-auth-container">
        <div className="brand-auth-card">
          <div className="brand-auth-header">
            <Link to="/">
              <img
                src={require('../assets/images/Y.png')}
                alt="Yemberzal"
                className="brand-auth-logo"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = '/default-image.avif';
                }}
              />
            </Link>
            <h1 className="brand-auth-title">Welcome, {brandInfo?.contact_name}!</h1>
            <p className="brand-auth-subtitle">
              Set up your password to access the <strong>{brandInfo?.brand_name}</strong> brand portal
            </p>
          </div>

          {error && (
            <div className="brand-alert brand-alert-error">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="brand-form-group">
              <label htmlFor="email" className="brand-form-label">Email Address</label>
              <input
                type="email"
                id="email"
                className="brand-form-input"
                value={brandInfo?.email || ''}
                disabled
                style={{ background: '#f0ebe5', cursor: 'not-allowed' }}
              />
              <p className="brand-form-hint">This will be your login email</p>
            </div>

            <div className="brand-form-group">
              <label htmlFor="password" className="brand-form-label">Create Password</label>
              <input
                type="password"
                id="password"
                name="password"
                className={`brand-form-input ${validationErrors.password ? 'error' : ''}`}
                placeholder="Create a strong password"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="new-password"
              />
              {validationErrors.password && (
                <p className="brand-form-error">{validationErrors.password}</p>
              )}
              <p className="brand-form-hint">Must be at least 8 characters</p>
            </div>

            <div className="brand-form-group">
              <label htmlFor="confirmPassword" className="brand-form-label">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                className={`brand-form-input ${validationErrors.confirmPassword ? 'error' : ''}`}
                placeholder="Confirm your password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                autoComplete="new-password"
              />
              {validationErrors.confirmPassword && (
                <p className="brand-form-error">{validationErrors.confirmPassword}</p>
              )}
            </div>

            <button
              type="submit"
              className="brand-btn brand-btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="brand-spinner" />
                  Setting up...
                </>
              ) : (
                'Set Password & Continue'
              )}
            </button>
          </form>

          <div className="brand-auth-footer">
            <Link to="/" style={{ color: 'inherit', textDecoration: 'none' }}>
              ← Back to Yemberzal
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BrandSetupPassword;
